import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useGroup } from "@/context/GroupContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CardSkeleton } from "@/components/Skeletons";

export default function EditExpense() {
  const { slug, expenseId } = useParams();
  const { groupId } = useGroup();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function load() {
      if (!groupId) return;
      const [{ data }, cats] = await Promise.all([
        api.get(`/expenses/${expenseId}`),
        api.get("/categories", { params: { group_id: groupId } }),
      ]);
      setTitle(data.title);
      setDescription(data.description || "");
      setCategoryId(data.category_id);
      setCategories(cats.data);
      setLoaded(true);
    }
    load();
  }, [expenseId, groupId]);

  const save = async () => {
    try {
      await api.patch(`/expenses/${expenseId}`, { title, description, category_id: categoryId });
      toast.success("Updated");
      navigate(`/${slug}/expenses/${expenseId}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!loaded) return <CardSkeleton />;

  return (
    <div className="space-y-5 lg:max-w-2xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Edit</div>
        <h1 className="heading text-2xl font-bold tracking-tight text-slate-900 mt-1">Expense info</h1>
        <p className="text-xs text-slate-500 mt-1">
          Amount, payer and members are locked once saved to keep the ledger accurate.
        </p>
      </div>

      <div className="bg-white rounded-3xl p-5 border border-slate-200 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="edit-title-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Category</Label>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {categories.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setCategoryId(c.id)}
                className={`p-3 rounded-2xl border text-center text-xs font-semibold ${
                  c.id === categoryId ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 rounded-2xl" data-testid="edit-description-input" />
        </div>
        <Button onClick={save} className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="edit-save-button">
          Save
        </Button>
      </div>
    </div>
  );
}
